import React, { useCallback, useState } from "react";
import { Button, Modal, Stack, Text, Image, Group } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { stickerService } from "../../services/stickerService";
import { bridgeService } from "../../services/bridgeService";
import { deviceBridgeService } from "../../services/deviceBridge.service";
import { IconSticker } from "../../theme/printColors";
import {
  formatDateOnlyWithShortMonth,
  getLabTodayDateOnly,
} from "../../utils/dateOnly";

const PREVIEW_POLL_INTERVAL_MS = 2000;
const PREVIEW_POLL_TIMEOUT_MS = 30000;

export function buildStickerTestPreviewPayload(now: Date = new Date()) {
  return {
    barcodeValue: "TEST-DYMO-001;T;1;1",
    fields: {
      caseNumber: "TEST-DYMO-001",
      specimen: "T",
      blockId: "1",
      slideId: "1",
      patientName: "Test, Patient",
      stain: "H&E",
      dateReceived: formatDateOnlyWithShortMonth(getLabTodayDateOnly(now), {
        twoDigitYear: true,
      }),
    },
  };
}

interface Props {
  deviceId: string;
  workstationId: string;
}

export const StickerPrinterTestPrintButton: React.FC<Props> = ({
  deviceId,
  workstationId,
}) => {
  const [loading, setLoading] = useState(false);
  const [previewBase64, setPreviewBase64] = useState<string | null>(null);

  const handleTestPrint = useCallback(async () => {
    setLoading(true);

    if (deviceBridgeService.isLocal()) {
      try {
        const result = await deviceBridgeService.sendCommand(
          {
            type: "render_sticker_preview",
            payload: { deviceId, ...buildStickerTestPreviewPayload() },
          },
          15000
        );
        const payload = result.payload ?? {};
        if (payload.success && payload.previewPngBase64) {
          setPreviewBase64(payload.previewPngBase64 as string);
          setLoading(false);
          return;
        }
        if (payload.success) {
          notifications.show({
            title: "Test Print Sent",
            message: "Sticker sent to printer.",
            color: "blue",
            autoClose: 4000,
          });
          setLoading(false);
          return;
        }
      } catch {
        // Local bridge failed — fall through to backend path
      }
    }

    try {
      const result = await stickerService.testPrint(workstationId, deviceId);
      const commandId: string = result.commandId;

      const pollInterval = setInterval(async () => {
        try {
          const cmd = await bridgeService.getCommand(commandId);
          const { status, result: cmdResult } = cmd.data;

          if (status === "completed") {
            clearInterval(pollInterval);
            setLoading(false);
            if (cmdResult?.previewPngBase64) {
              setPreviewBase64(cmdResult.previewPngBase64);
            } else {
              notifications.show({
                title: "Test Print Sent",
                message: "Sticker sent to printer.",
                color: "blue",
                autoClose: 4000,
              });
            }
          } else if (status === "failed" || status === "expired") {
            clearInterval(pollInterval);
            setLoading(false);
            notifications.show({
              title: "Test Print Failed",
              message: "The printer did not confirm the test print.",
              color: "red",
              autoClose: 5000,
            });
          }
        } catch {
          clearInterval(pollInterval);
          setLoading(false);
        }
      }, PREVIEW_POLL_INTERVAL_MS);

      setTimeout(() => {
        clearInterval(pollInterval);
        setLoading(false);
      }, PREVIEW_POLL_TIMEOUT_MS);
    } catch {
      setLoading(false);
      notifications.show({
        title: "Test Print Failed",
        message: "Could not dispatch test print. Please try again.",
        color: "red",
        autoClose: 5000,
      });
    }
  }, [deviceId, workstationId]);

  const handleClosePreview = useCallback(() => {
    setPreviewBase64(null);
  }, []);

  const handleVerifiedScannable = useCallback(() => {
    notifications.show({
      title: "Verified",
      message: "Test print marked as scannable.",
      color: "green",
      autoClose: 4000,
    });
    setPreviewBase64(null);
  }, []);

  return (
    <>
      <Button
        leftSection={<IconSticker size={16} />}
        color="violet"
        variant="light"
        size="xs"
        onClick={handleTestPrint}
        loading={loading}
      >
        Test Print
      </Button>

      <Modal
        opened={!!previewBase64}
        onClose={handleClosePreview}
        title="Test Print Preview"
      >
        <Stack align="center">
          <Text size="sm">The following label was sent to the printer:</Text>
          {previewBase64 && (
            <Image
              src={`data:image/png;base64,${previewBase64}`}
              alt="Sticker Preview"
              w={200}
              h={200}
              style={{ border: "1px solid var(--mantine-color-gray-3)" }}
            />
          )}
          <Group mt="md">
            <Button variant="default" onClick={handleClosePreview}>
              Close
            </Button>
            <Button color="green" onClick={handleVerifiedScannable}>
              Verified Scannable
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
